import React, { useState } from "react";
import { FaArrowRight, FaRegCirclePlay, FaLayerGroup, FaGear, FaCode, FaShieldHalved, FaHeadset } from "react-icons/fa6";
import { Container } from "../home/ui";

// Hero artwork (CMS dashboard on a laptop + phone). On desktop (xl+) it sits as a background on the
// right side of the section; on mobile/tablet it shows below the text.
// Put the file at public/img/cms-hero.jpg (or change the path). Until it exists, the custom-website
// hero art is shown instead.
const heroImg = "/img/cms-hero.jpg";
const fallbackImg = "/img/custom-website-hero.jpg";

const features = [
  { icon: FaGear, title: "User-Friendly CMS", text: "Manage content with ease", tone: "bg-[#E8F0FF] text-[#1E5BFF]" },
  { icon: FaCode, title: "Custom Design", text: "Tailored to your brand", tone: "bg-[#EEEBFF] text-[#4F46E5]" },
  { icon: FaShieldHalved, title: "Secure & Scalable", text: "Built for performance and growth", tone: "bg-[#E8F0FF] text-[#1E5BFF]" },
  { icon: FaHeadset, title: "Ongoing Support", text: "We're here after launch", tone: "bg-[#F3E8FF] text-[#9333EA]" },
];

// Fades all four edges of the image so its own background never shows as a box.
const fadeX = "linear-gradient(to right, transparent 0%, #000 10%, #000 94%, transparent 100%)";
const fadeY = "linear-gradient(to bottom, transparent 0%, #000 8%, #000 92%, transparent 100%)";
const edgeFade = {
  WebkitMaskImage: `${fadeX}, ${fadeY}`,
  WebkitMaskComposite: "source-in",
  maskImage: `${fadeX}, ${fadeY}`,
  maskComposite: "intersect",
};

const CmsHero = () => {
  const [src, setSrc] = useState(heroImg);
  const onError = () => src !== fallbackImg && setSrc(fallbackImg);

  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-[#FCFCFE] via-[#F7F9FF] to-[#EFF3FE] pb-[48px] pt-[110px] xl:pb-[44px] xl:pt-[calc(clamp(76px,5.83vw,112px)+28px)]">
      {/* Desktop: artwork as background on the right (natural ratio, never stretched or cropped) */}
      <img
        src={src}
        onError={onError}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-[calc(clamp(76px,5.83vw,112px)+4px)] hidden h-[min(34vw,640px)] w-auto max-w-none select-none xl:block"
        style={edgeFade}
      />

      <Container className="relative grid max-w-[1900px] items-center gap-[32px] xl:grid-cols-[minmax(0,42%)_1fr] xl:pl-[clamp(32px,4.375vw,84px)] xl:pr-[clamp(24px,3.125vw,60px)]">
        <div>
          <span className="inline-flex items-center gap-[8px] rounded-full bg-[#E6EEFF] px-[16px] py-[7px] text-[13.5px] font-medium text-[#1E3FB8]">
            <FaLayerGroup className="text-[14px] text-[#1E5BFF]" />
            CMS Website Development
          </span>

          <h1 className="mt-[20px] text-[38px] font-bold leading-[1.1] tracking-tight text-[#0B1B4D] sm:text-[52px] xl:text-[clamp(42px,3.5vw,66px)]">
            Easy to Manage
            <br />
            <span className="text-gradient xl:whitespace-nowrap">Powerful Websites</span>
            <br />
            with CMS
          </h1>

          <p className="mt-[18px] max-w-[580px] text-[16px] leading-[1.6] text-slate-600 sm:text-[17px] xl:text-[clamp(15px,1.05vw,18px)]">
            We build flexible, scalable, and fully manageable CMS websites that give you complete control over your
            content. Update pages, add blogs, manage media and grow your business without any technical expertise.
          </p>

          <div className="mt-[28px] flex flex-wrap items-center gap-[16px]">
            <a
              href="/contact-us"
              className="group inline-flex h-[54px] items-center gap-[10px] rounded-full bg-gradient-to-r from-[#0E7BFF] via-[#3D5BF6] to-[#A21CF0] px-[28px] text-[16px] font-medium text-white shadow-[0_14px_30px_-14px_rgba(91,61,245,0.7)] transition hover:brightness-110"
            >
              Get a Free Consultation
              <FaArrowRight className="text-[14px] transition group-hover:translate-x-1" />
            </a>
            <a
              href="/our-completed-projects"
              className="inline-flex h-[54px] items-center gap-[10px] rounded-full border-[1.5px] border-solid border-[#9DB6FF] bg-white/70 px-[28px] text-[16px] font-medium text-[#1E3FB8] transition hover:bg-[#EEF3FF]"
            >
              <FaRegCirclePlay className="text-[22px]" />
              View Demo
            </a>
          </div>
        </div>

        {/* Mobile / tablet: artwork below the text */}
        <div className="flex justify-center xl:hidden">
          <img
            src={src}
            onError={onError}
            alt="CMS dashboard on a laptop and a website on a phone"
            className="h-auto w-full max-w-[760px] select-none object-contain"
            style={edgeFade}
          />
        </div>
      </Container>

      {/* Feature strip */}
      <Container className="relative mt-[28px] max-w-[1900px] xl:pl-[clamp(32px,4.375vw,84px)] xl:pr-[clamp(24px,3.125vw,60px)]">
        <div className="grid max-w-[1020px] grid-cols-1 gap-[22px] sm:grid-cols-2 lg:grid-cols-4 lg:gap-0 xl:max-w-[min(1100px,76%)]">
          {features.map((f, i) => (
            <div
              key={f.title}
              className={`flex min-w-0 items-start gap-[12px] lg:py-[6px] lg:pr-[14px] ${
                i === 0 ? "" : "lg:border-y-0 lg:border-l-[1px] lg:border-r-0 lg:border-solid lg:border-[#E3E8F3] lg:pl-[18px]"
              }`}
            >
              <span className={`inline-flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full text-[18px] ${f.tone}`}>
                <f.icon />
              </span>
              <div className="min-w-0">
                <p className="text-[14.5px] font-semibold leading-[1.3] text-[#0B1B4D] xl:text-[clamp(13px,0.95vw,15px)]">{f.title}</p>
                <p className="mt-[5px] text-[13.5px] leading-[1.5] text-slate-500 xl:text-[clamp(12px,0.9vw,14px)]">{f.text}</p>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default CmsHero;
